import { Suspense, lazy, useEffect, useRef, useSyncExternalStore } from 'react';
import { NavLink, Route, Routes, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import Dashboard from './pages/Dashboard.jsx';
import Inventory from './pages/Inventory.jsx';
import Capture from './pages/Capture.jsx';
import ReceiptReview from './pages/ReceiptReview.jsx';
import ScanReview from './pages/ScanReview.jsx';
import Recipes from './pages/Recipes.jsx';
import DiscoverRecipes from './pages/DiscoverRecipes.jsx';
import RecipeDetail from './pages/RecipeDetail.jsx';
import RecipeEditor from './pages/RecipeEditor.jsx';
import CookNow from './pages/CookNow.jsx';
import ShoppingList from './pages/ShoppingList.jsx';
import MealPlan from './pages/MealPlan.jsx';
import Items from './pages/Items.jsx';
import ItemDetail from './pages/ItemDetail.jsx';
import Settings from './pages/Settings.jsx';
import Join from './pages/Join.jsx';
import FirstRun from './pages/FirstRun.jsx';
import Welcome from './pages/Welcome.jsx';
import { Loading, Announcer, announce } from './components/ui.jsx';
import { useApi, useAuth } from './lib/hooks.js';
import { sawNoHousehold, subscribeNoHousehold } from './lib/api.js';
import { signOut } from './lib/auth.js';

/**
 * The barcode scanner is the one route loaded on demand. Its decoder is larger
 * than the rest of the app put together, and a phone should not pay for it on
 * every cold start to reach a screen most visits never open.
 */
const ScanBarcode = lazy(() => import('./pages/ScanBarcode.jsx'));

const Icon = ({ path }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {path}
  </svg>
);

const NAV = [
  {
    to: '/', label: 'Home', end: true,
    icon: <Icon path={<><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V20h14V9.5" /></>} />,
  },
  {
    to: '/inventory', label: 'Pantry',
    icon: <Icon path={<><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M4 12h16" /><path d="M9 7.5h1" /><path d="M9 16.5h1" /></>} />,
  },
  {
    to: '/capture', label: 'Scan',
    icon: <Icon path={<><path d="M4 8V6a2 2 0 0 1 2-2h2" /><path d="M16 4h2a2 2 0 0 1 2 2v2" /><path d="M20 16v2a2 2 0 0 1-2 2h-2" /><path d="M8 20H6a2 2 0 0 1-2-2v-2" /><circle cx="12" cy="12" r="3" /></>} />,
  },
  {
    to: '/recipes', label: 'Recipes',
    icon: <Icon path={<><path d="M4 4.5A1.5 1.5 0 0 1 5.5 3H19v18H5.5A1.5 1.5 0 0 1 4 19.5Z" /><path d="M8 3v18" /><path d="M12 8h4" /><path d="M12 12h4" /></>} />,
  },
  {
    to: '/shopping', label: 'List',
    icon: <Icon path={<><path d="M4 5h2l2.2 10.4a2 2 0 0 0 2 1.6h6.4a2 2 0 0 0 2-1.55L20.5 8H7" /><circle cx="10" cy="20" r="1.2" /><circle cx="17" cy="20" r="1.2" /></>} />,
  },
];

const TITLES = [
  [/^\/$/, 'Pantry'],
  [/^\/welcome/, 'Welcome'],
  [/^\/inventory/, 'Inventory'],
  [/^\/capture/, 'Scan'],
  [/^\/scan-barcode/, 'Scan a barcode'],
  [/^\/receipts\//, 'Review receipt'],
  [/^\/scans\//, 'Review scan'],
  [/^\/recipes\/discover/, 'Find a recipe'],
  [/^\/recipes\/new/, 'New recipe'],
  // Ids are uuids, not digits. \d+ here survived the integer era and quietly
  // mistitled every detail screen -- including what a screen reader announces.
  [/^\/recipes\/[^/]+\/edit/, 'Edit recipe'],
  [/^\/recipes\/[^/]+/, 'Recipe'],
  [/^\/recipes/, 'Recipes'],
  [/^\/cook-now/, 'Cook now'],
  [/^\/shopping/, 'Shopping list'],
  [/^\/meal-plan/, 'Meal plan'],
  [/^\/items\/[^/]+/, 'Item'],
  [/^\/items/, 'Master items'],
  [/^\/settings/, 'Settings'],
];

export default function App() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const title = TITLES.find(([pattern]) => pattern.test(pathname))?.[1] || 'Pantry';

  // A link click starts the new screen at the top. Without this the router
  // keeps the old scroll offset, so tapping into a recipe from the bottom of a
  // long list opens it mid-page. POP is left alone: on back/forward the
  // browser's own restored position is the right one.
  useEffect(() => {
    if (navigationType !== 'POP') window.scrollTo(0, 0);
  }, [pathname, navigationType]);

  // Signing in no longer creates a household, so an account can exist without
  // one -- most often because the person was invited and has not accepted yet.
  // Asked of the one route a household-less client is certain to be allowed to
  // call, rather than inferred from a 403: a client that learns its own state
  // from an error mistakes an outage for an empty account.
  // The URL and the heading change while focus stays on the link that was
  // clicked, so nothing tells a screen reader user they have arrived anywhere.
  // Announced rather than focus-moved: stealing focus mid-interaction is more
  // disorienting than the silence it fixes.
  useEffect(() => { announce(title); }, [title]);

  const auth = useAuth();
  const bootstrap = useApi('/household/pending');

  /**
   * Two ways to learn the same fact, because one of them can fail.
   *
   * `/household/pending` is the primary source and stays so: it is the one
   * route a household-less client is certain to be allowed to call, and asking
   * it directly is what keeps an outage from being read as an empty account.
   *
   * The backstop is for when that request does not answer at all. Undefined is
   * not false, so `has_household === false` was false, and the app concluded
   * the account had a pantry -- then rendered a shell where every screen 403s
   * and nothing offers a way to make one. The escape hatch was signing out.
   *
   * Any endpoint refusing with NO_HOUSEHOLD is the server stating the fact
   * outright, which is evidence of exactly the same quality. Only that code
   * counts; a timeout or a 500 states nothing and is ignored, which is the
   * distinction the primary source exists to protect.
   */
  const refused = useSyncExternalStore(subscribeNoHousehold, sawNoHousehold);
  const needsHousehold = bootstrap.data?.has_household === false || refused;

  /**
   * First arrival, once: somebody who has a pantry and has not been shown
   * around gets the tour.
   *
   * Only from "/", and only once per tab session. Redirecting from anywhere
   * else would hijack deep links -- an invitation, a notification, a bookmarked
   * recipe -- and sending somebody to a tour instead of the thing they asked
   * for is worse than never showing them the tour at all.
   *
   * sessionStorage rather than a ref, because a ref dies with this component
   * and this component is unmounted by any route OUTSIDE the gate: reading the
   * manual at /help mid-tour and coming back would remount App with a fresh ref
   * and forward to the tour again, when the person was plainly somewhere else
   * on purpose. Wrapped in try/catch -- storage can be unavailable, and the
   * cost of that is one extra forwarding, not an error.
   */
  const toured = useRef(false);
  useEffect(() => {
    try { toured.current = toured.current || sessionStorage.getItem('pantry:toured') === '1'; } catch { /* storage refused */ }
    if (toured.current) return;
    if (bootstrap.data?.onboarded !== false) return;
    // A household-less account is mid-signup and gets the first-run screen
    // below; the tour has nothing to show somebody with no pantry yet.
    if (bootstrap.data?.has_household === false) return;
    if (pathname !== '/') return;
    toured.current = true;
    try { sessionStorage.setItem('pantry:toured', '1'); } catch { /* storage refused */ }
    // Replaced rather than pushed: an entry that exists only to forward is one
    // the Back button should not have to walk through.
    navigate('/welcome', { replace: true });
  }, [bootstrap.data, pathname, navigate]);

  // /join carries a token and does its own accepting, so it is left alone.
  // Sending somebody holding an invitation link to a screen asking for a code
  // would be asking them for something they already have.
  if (needsHousehold && pathname !== '/join') {
    return (
      <div className="app">
        <header className="app-header">
          <h1>Pantry</h1>
          {/* The full app's escape hatches live in Settings, which needs a
              household to mean anything. Somebody parked here on the wrong
              account still has to be able to leave it. */}
          {auth.status === 'signed-in' && (
            <button type="button" className="small ghost" onClick={() => signOut()}>Sign out</button>
          )}
        </header>
        <main className="app-main">
          {/* Into the tour rather than the home screen. Somebody who has just
              made or joined a pantry is the exact person the tour is for, and a
              full navigation is what gets /household/pending asked again with
              the new membership in it. */}
          <FirstRun onReady={() => window.location.assign('/welcome')} />
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <h1>{title}</h1>
        <NavLink to="/settings" className="button icon ghost" aria-label="Settings">
          <Icon path={<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1 1.56V21a2 2 0 0 1-4 0v-.09A1.7 1.7 0 0 0 8.9 19.3a1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.56-1H3a2 2 0 0 1 0-4h.09A1.7 1.7 0 0 0 4.7 8.9a1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34H9.1a1.7 1.7 0 0 0 1-1.56V3a2 2 0 0 1 4 0v.09a1.7 1.7 0 0 0 1 1.56 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87v.04a1.7 1.7 0 0 0 1.56 1H21a2 2 0 0 1 0 4h-.09a1.7 1.7 0 0 0-1.51 1Z" /></>} />
        </NavLink>
      </header>

      <Announcer />

      <main className="app-main">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/welcome" element={<Welcome />} />
          {/* Inside the gate: joining needs an account, and the gate returns
              here with the token intact once a session lands. */}
          <Route path="/join" element={<Join />} />
          <Route path="/inventory" element={<Inventory />} />
          <Route path="/capture" element={<Capture />} />
          <Route
            path="/scan-barcode"
            element={<Suspense fallback={<Loading />}><ScanBarcode /></Suspense>}
          />
          <Route path="/receipts/:id" element={<ReceiptReview />} />
          <Route path="/scans/:id" element={<ScanReview />} />
          <Route path="/recipes" element={<Recipes />} />
          <Route path="/recipes/discover" element={<DiscoverRecipes />} />
          <Route path="/recipes/new" element={<RecipeEditor />} />
          <Route path="/recipes/:id" element={<RecipeDetail />} />
          <Route path="/recipes/:id/edit" element={<RecipeEditor />} />
          <Route path="/cook-now" element={<CookNow />} />
          <Route path="/shopping" element={<ShoppingList />} />
          <Route path="/meal-plan" element={<MealPlan />} />
          <Route path="/items" element={<Items />} />
          <Route path="/items/:id" element={<ItemDetail />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<div className="empty"><h3>Not found</h3></div>} />
        </Routes>
      </main>

      <nav className="app-nav" aria-label="Main">
        {NAV.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end}>
            {item.icon}
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
